// Entry point of the marketing site (jungleai.com): the landing page, blog, use-case pages and pricing.
//
// The app (index.tsx → renderJungleWebApp) boots Firebase, the stores, Tolgee and Sentry before it
// can show anything: fine for a signed-in product, far too slow for an ad landing page. This entry mounts
// only what the marketing pages use (Chakra, the router, react-helmet and OpenPanel), so a visitor sees
// the page after a fraction of the JavaScript. The pages themselves are shared with the app: /testing
// still renders the same components inside the app for previews, and MarketingSiteRouter is the same.
//
// Vercel serves this entry's HTML (build/marketing.html, filled in with each page's rendering by
// scripts/generate-marketing-static-html.mjs) for every marketing route, and middleware.ts maps
// jungleai.com/ to it as well.
import React from "react";
import { trackErrorWhenLoaded } from "./utils/analytics/track-error-when-loaded";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ChakraProvider, ColorModeScript } from "@chakra-ui/react";
import { HelmetProvider } from "react-helmet-async";
import { marketingSiteChakraTheme } from "./pages/marketing-site/marketingSiteChakraTheme";
import MarketingSiteRouter from "./pages/marketing-site/MarketingSiteRouter";
import { initializeOpenPanelForJungleWebApp } from "./utils/analytics/openpanel-client";
import { hasPrerenderedMarketingPageForCurrentUrl } from "./pages/marketing-site/marketingSiteRoutes";
import { getFrontendRelease } from "./utils/analytics/frontend-release";

// OpenPanel receives the page view as soon as the page renders, so it is set up before the first render.
// Sentry is not on the critical path: it loads once the page is idle (see below).
document.documentElement.dataset.frontendRelease = getFrontendRelease();
initializeOpenPanelForJungleWebApp();

const marketingSiteTree = (
  <HelmetProvider>
    <ColorModeScript initialColorMode={marketingSiteChakraTheme.config.initialColorMode} />
    <ChakraProvider theme={marketingSiteChakraTheme}>
      <BrowserRouter>
        <MarketingSiteRouter />
      </BrowserRouter>
    </ChakraProvider>
  </HelmetProvider>
);

const rootElement = document.getElementById("root") as HTMLElement;
if (hasPrerenderedMarketingPageForCurrentUrl()) {
  // The static HTML is what this tree renders (src/marketing-prerender.tsx renders the same tree at build
  // time), so React adopts it instead of replacing it: nothing is re-created, so the first paint's text
  // and images stay the page's largest contentful paint, and the page becomes interactive in place.
  // Should the markup ever differ, React falls back to rendering from scratch and reports it here. The
  // report goes to Sentry only (nothing reaches the console in production), so <html> is also marked
  // `data-marketing-hydration="recovered"`: the signal the browser check (tests/ui/scenarios/marketing.spec.ts)
  // and a person in DevTools can see.
  ReactDOM.hydrateRoot(rootElement, marketingSiteTree, {
    onRecoverableError: (error) => {
      document.documentElement.dataset.marketingHydration = "recovered";
      trackErrorWhenLoaded(error, { scope: "marketing-hydration", level: "warning" });
    },
  });
} else {
  ReactDOM.createRoot(rootElement).render(marketingSiteTree);
}

// Error reporting (the same cost-tuned Sentry setup as the app) is loaded after the page has painted:
// Explicit upload and hydration errors initialize reporting immediately, including before this idle task.
function loadErrorReportingWhenIdle() {
  void import("./utils/analytics/marketing-web-vitals").then(({ initializeMarketingWebVitals }) => initializeMarketingWebVitals()).catch(() => {});
  const loadErrorReporting = () =>
    import("./utils/analytics/sentry-init").then(({ initializeSentryForJungleWebApp }) =>
      initializeSentryForJungleWebApp()
    );
  if (typeof window.requestIdleCallback === "function")
    window.requestIdleCallback(() => void loadErrorReporting(), { timeout: 4000 });
  else window.setTimeout(() => void loadErrorReporting(), 2000);
}
if (document.readyState === "complete") loadErrorReportingWhenIdle();
else window.addEventListener("load", loadErrorReportingWhenIdle, { once: true });
